import { ref, computed, watch, nextTick, onMounted, resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, createCommentVNode, renderSlot, withDirectives, createVNode, normalizeStyle, vShow, toDisplayString } from 'vue';
import BScroll from '@better-scroll/core';
import PullDown from '@better-scroll/pull-down';
import Pullup from '@better-scroll/pull-up';
import Loading from './loading';
import propTypes from './utils/prop-types';
import { createComponent } from './utils/create-component';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var props = {
  /**
   * 需要被监听的变化的数据
   */
  data: propTypes.oneOfType([propTypes.array(), propTypes.object()]).def([]),
  /**
   * BetterScroll配置项
   */
  options: propTypes.object().re(),
  /**
   * 需要监听的事件，会对外抛出
   */
  scrollEvents: propTypes.array().def([]),
  /**
   * 下拉加载额外配置
   */
  pullDownOptions: propTypes.object().def({
    successText: '加载成功'
  }),
  /**
   * 上拉加载额外配置
   */
  pullUpOptions: propTypes.object().def({
    pullUpText: '上拉加载'
  })
};
var emits = ['refresh', 'enable', 'disable', 'beforeScrollStart', 'scrollStart', 'scroll', 'scrollEnd', 'scrollCancel', 'touchEnd', 'flick', 'destroy', 'contentChanged', 'pullingDown', 'pullingUp'];

var _createComponent = createComponent('scroll'),
  create = _createComponent.create,
  className = _createComponent.className;
// 默认回弹动画时长
var TIME_BOUNCE = 300;
// 默认下拉刷新距离
var DEFAULT_THRESHOLD = 90;
BScroll.use(PullDown);
BScroll.use(Pullup);
var script = create({
  components: _defineProperty({}, Loading.name, Loading),
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var emit = _ref.emit;
    // 默认回弹时长
    var BOUNCE_TIME = props.options.bounceTime || TIME_BOUNCE;
    // 需要绑定的根节点
    var scrollContainerRef = ref();
    // bs实例
    var bsInstance = ref();
    // 当前滚动的相对位置
    var scrollPos = ref({
      x: 0,
      y: 0
    });
    /**
     * pullDown相关
     */
    //  是否在完成pullDown状态前
    var beforePullDown = ref(true);
    // 是否在下拉处理中
    var isPullDownProcessing = ref(false);
    /**
     * pullUp相关
     */
    //  是否在上拉更新中
    var isPullUpLoadProcessing = ref(false);
    // 是否使用pullUp插件
    var isOpenPullUp = computed(function () {
      return props.options && !!props.options.pullUpLoad;
    });
    // 是否使用pullDown插件
    var isOpenPullDown = computed(function () {
      return props.options && !!props.options.pullDownRefresh;
    });
    // 下拉阈值
    var threshold = computed(function () {
      return props.options.pullDownRefresh && props.options.pullDownRefresh.threshold || DEFAULT_THRESHOLD;
    });
    // 下拉加载的loading百分比
    var loadingPercent = computed(function () {
      var y = (scrollPos.value.y - 0.5 * threshold.value) / (0.5 * threshold.value);
      var FULL_OFFSET = 160; // loading图整体走完一圈(从无到完整的图形)的offset值
      if (y <= 0) {
        return FULL_OFFSET;
      }
      if (y < 1) {
        return Math.floor(FULL_OFFSET + FULL_OFFSET * y);
      }
      return FULL_OFFSET * 2;
    });
    /**
     * 实例化BScroll
     */
    function initScroll() {
      bsInstance.value = new BScroll(scrollContainerRef.value, _objectSpread2({
        bounceTime: BOUNCE_TIME
      }, props.options));
    }
    /**
     * 绑定内置事件
     */
    function bindBaseEvents() {
      isOpenPullDown.value && bsInstance.value.on('pullingDown', pullingDownHandler);
      isOpenPullUp.value && bsInstance.value.on('pullingUp', pullingUpHandler);
      bsInstance.value.on('scroll', scrollHandler);
    }
    /**
     * 绑定用户传入
     */
    function bindCustomEvents() {
      var _arguments = arguments;
      var instance = bsInstance.value;
      props.scrollEvents.map(function (eventName) {
        instance.on(eventName, function () {
          var callback = function callback() {};
          if (eventName === 'pullingDown') {
            callback = finishPullDown;
          }
          if (eventName === 'pullingUp') {
            callback = finishPullUp;
          }
          emit(eventName, _objectSpread2(_objectSpread2({}, _arguments), {}, {
            finishFn: callback
          }));
        });
      });
    }
    /**
     * 滚动handler
     */
    function scrollHandler(pos) {
      scrollPos.value = pos;
    }
    /**
     * 下拉handler
     */
    function pullingDownHandler() {
      beforePullDown.value = false;
      isPullDownProcessing.value = true;
    }
    /**
     * 上拉handler
     */
    function pullingUpHandler() {
      isPullUpLoadProcessing.value = true;
    }
    /**
     * 内部完成pulldown后调用
     */
    function _finishPullDown() {
      bsInstance.value.finishPullDown();
      setTimeout(function () {
        bsInstance.value.refresh();
        beforePullDown.value = true;
      }, BOUNCE_TIME + 100);
    }
    /**
     * 外部获取BScroll实例
     */
    function getBsInstance() {
      return bsInstance.value;
    }
    /**
     * 外部完成pulldown后调用
     */
    function finishPullDown() {
      isPullDownProcessing.value = false;
      _finishPullDown();
    }
    /**
     * 外部完成pullup后调用
     */
    function finishPullUp() {
      bsInstance.value.finishPullUp();
      setTimeout(function () {
        bsInstance.value.refresh();
        isPullUpLoadProcessing.value = false;
      }, BOUNCE_TIME + 100);
    }
    /**
     * 强制刷新，对外提供刷新方法
     */
    function forceUpdate() {
      nextTick(function () {
        bsInstance.value && bsInstance.value.refresh && bsInstance.value.refresh();
      });
    }
    watch(function () {
      return props.data;
    }, function () {
      nextTick(function () {
        bsInstance.value && bsInstance.value.refresh();
      });
    }, {
      deep: true
    });
    onMounted(function () {
      // 实例化BScroll
      initScroll();
      // 绑定内置事件
      bindBaseEvents();
      // 绑定自定义事件
      bindCustomEvents();
    });
    return {
      className: className,
      scrollPos: scrollPos,
      loadingPercent: loadingPercent,
      beforePullDown: beforePullDown,
      isPullDownProcessing: isPullDownProcessing,
      isPullUpLoadProcessing: isPullUpLoadProcessing,
      isOpenPullUp: isOpenPullUp,
      isOpenPullDown: isOpenPullDown,
      scrollContainerRef: scrollContainerRef,
      scrollHandler: scrollHandler,
      getBsInstance: getBsInstance,
      finishPullDown: finishPullDown,
      finishPullUp: finishPullUp,
      forceUpdate: forceUpdate
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_loading = resolveComponent("sp-loading");
  return openBlock(), createElementBlock("div", {
    "class": normalizeClass(_ctx.className)
  }, [createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__wrapper")),
    ref: "scrollContainerRef"
  }, [createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__pulldown_scroller"))
  }, [createCommentVNode(" pulldown部分 "), _ctx.isOpenPullDown ? (openBlock(), createElementBlock("div", {
    key: 0,
    "class": normalizeClass("".concat(_ctx.className, "__pulldown_wrapper"))
  }, [renderSlot(_ctx.$slots, "pulldown", {
    isOpenPulldown: _ctx.isOpenPullDown,
    scrollPos: _ctx.scrollPos
  }, function () {
    return [withDirectives(createElementVNode("div", null, [createVNode(_component_sp_loading, {
      type: "default",
      anim: false,
      style: normalizeStyle({
        strokeDashoffset: _ctx.loadingPercent
      })
    }, null, 8 /* PROPS */, ["style"])], 512 /* NEED_PATCH */), [[vShow, _ctx.beforePullDown]]), withDirectives(createElementVNode("div", null, [withDirectives(createElementVNode("div", null, [createVNode(_component_sp_loading, {
      type: "default"
    })], 512 /* NEED_PATCH */), [[vShow, _ctx.isPullDownProcessing]]), withDirectives(createElementVNode("div", null, toDisplayString(_ctx.pullDownOptions.successText), 513 /* TEXT, NEED_PATCH */), [[vShow, !_ctx.isPullDownProcessing]])], 512 /* NEED_PATCH */), [[vShow, !_ctx.beforePullDown]])];
  })], 2 /* CLASS */)) : createCommentVNode("v-if", true), createCommentVNode(" 滚动部分 "), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__content"))
  }, [renderSlot(_ctx.$slots, "default")], 2 /* CLASS */), createCommentVNode(" 上拉刷新部分 "), _ctx.isOpenPullUp ? (openBlock(), createElementBlock("div", {
    key: 1,
    "class": normalizeClass("".concat(_ctx.className, "__pullup_wrapper"))
  }, [renderSlot(_ctx.$slots, "pullup", {
    isOpenPullUp: _ctx.isOpenPullUp,
    scrollPos: _ctx.scrollPos
  }, function () {
    return [!_ctx.isPullUpLoadProcessing ? (openBlock(), createElementBlock("div", {
      key: 0,
      "class": normalizeClass("".concat(_ctx.className, "__pullup_before_trigger"))
    }, [createElementVNode("span", {
      "class": normalizeClass("".concat(_ctx.className, "__pullup_content"))
    }, toDisplayString(_ctx.pullUpOptions.pullUpText), 3 /* TEXT, CLASS */)], 2 /* CLASS */)) : (openBlock(), createElementBlock("div", {
      key: 1,
      "class": normalizeClass("".concat(_ctx.className, "__pullup_after_trigger"))
    }, [createVNode(_component_sp_loading, {
      type: "default"
    })], 2 /* CLASS */))];
  })], 2 /* CLASS */)) : createCommentVNode("v-if", true)], 2 /* CLASS */)], 2 /* CLASS */)], 2 /* CLASS */);
}

script.render = render;
script.__file = "components/src/component/scroll/scroll.vue";

var _Scroll = script;

export { _Scroll as default };
